import React, { useState } from 'react';
import {
  useDeleteGridJobConfig,
  useGetGridJobConfigJobInstancesByIdWithLoading,
} from '@ais-ui/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  CommandItem,
  Popover,
  PopoverTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { GPSLostSelectSlider } from './GPSLostSelectSlider';
import { PiTrash } from 'react-icons/pi';
import { useLocalization } from '@ais-ui/locale-management';
import { useGPSLost } from './GPSLostContext';

interface GpsLostConfigurationListProps {
  gpsLostConfigurations: any[];
}

export const GpsLostConfigurationList = ({
  gpsLostConfigurations,
}: GpsLostConfigurationListProps) => {
  const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<null | number>(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const { toast } = useToast();
  const { messages } = useLocalization();

  const { mutate: deleteGpsLostConfiguration } = useDeleteGridJobConfig();

  const openDeleteConfirmation = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setConfigToDelete(id);
    setShowDeleteAlert(true);
  };

  const handleDeleteConfirm = async () => {
    if (configToDelete === null) return;

    try {
      setIsDeleting(true);
      deleteGpsLostConfiguration(configToDelete);
      toast({
        className: 'delete-gps-lost-configuration-success',
        title: messages.genericToastTitleSuccess,
        description: messages.gpsLostSuccessToDeleteConfiguration,
      });
    } catch (error) {
      console.error('Error deleting GPS Lost configuration:', error);
      toast({
        className: 'delete-gps-lost-configuration-failed',
        title: messages.genericToastTitleError,
        description: messages.gpsLostFailedToDeleteConfiguration,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteAlert(false);
      setConfigToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteAlert(false);
    setConfigToDelete(null);
  };

  const { setInstances } = useGPSLost();

  const [selectedConfiguration, setSelectedConfiguration] = useState<
    null | any
  >(null);

  const { fetchAllInstances, isLoadingAllPages, loadingProgress } =
    useGetGridJobConfigJobInstancesByIdWithLoading();

  const onSelectGpsLostConfiguration = async (configuration: any) => {
    setSelectedConfiguration(configuration);
    // Toggle popover if clicking the same item
    if (openPopoverId === configuration.id) {
      setOpenPopoverId(null);
      return;
    }

    setIsLoading(true);
    setOpenPopoverId(configuration.id);

    try {
      const configurationInstances = await fetchAllInstances(configuration.id);
      setInstances(configurationInstances.items);
    } catch (error) {
      console.error('Error fetching GPS Lost configuration details:', error);
      setOpenPopoverId(null); // Close popover on error
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {gpsLostConfigurations.map((gpsLostConfiguration) => (
        <Popover
          key={gpsLostConfiguration.uuid}
          open={openPopoverId === gpsLostConfiguration.id}
          onOpenChange={(open) => {
            if (!open) setOpenPopoverId(null);
          }}
        >
          <PopoverTrigger asChild>
            <CommandItem
              value={gpsLostConfiguration.id}
              onSelect={() =>
                onSelectGpsLostConfiguration(gpsLostConfiguration)
              }
              className="w-full cursor-pointer rounded-md"
            >
              <div className="flex justify-between w-full items-center">
                <div>{gpsLostConfiguration.job_name}</div>
                <Button
                  className="p-1 rounded-md hover:bg-slate-200 transition-colors"
                  onClick={(e) =>
                    openDeleteConfirmation(e, gpsLostConfiguration.id)
                  }
                  disabled={isDeleting}
                  aria-label="Delete configuration"
                  variant="ghost"
                  size="icon"
                >
                  <PiTrash />
                </Button>
              </div>
            </CommandItem>
          </PopoverTrigger>
          {selectedConfiguration && (
            <GPSLostSelectSlider
              configuration={selectedConfiguration}
              isLoading={isLoading}
            ></GPSLostSelectSlider>
          )}
        </Popover>
      ))}

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {messages.gpsLostDeleteConfirmationTitle || 'Confirm Deletion'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {messages.gpsLostDeleteConfirmationDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>
              {messages.genericCancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              {messages.genericDelete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
